/**
 * SubscribePage — Stripe Elements subscription checkout.
 *
 * Flow:
 *   1. Fetch plans from backend. User picks plan + billing cycle.
 *   2. POST /billing/setup-intent → clientSecret for card collection.
 *   3. Mount <StripeProvider> with the SetupIntent clientSecret.
 *   4. User enters card via <PaymentElement>, clicks Subscribe.
 *   5. stripe.confirmSetup() → paymentMethodId.
 *   6. POST /billing/subscription with { plan, billingCycle, paymentMethodId }.
 *   7. If SCA required, confirmPayment() with the returned clientSecret.
 *   8. Poll /auth/me until plan lands, then navigate to /billing.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { StripeProvider } from "@/components/billing/StripeProvider.tsx";
import { billingApi } from "@/api/billing.ts";
import { authApi } from "@/api/auth.ts";
import { useAuth } from "@/hooks/useAuth.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { useToast } from "@/stores/toastStore.ts";
import type { PaidPlanTier } from "@vms/tubeforge-shared";
import { annualOffered, annualSaving, shownPrice, shownMaxLength } from "./plan-price.ts";

type Cycle = "monthly" | "annually";

// ── Helpers ─────────────────────────────────────────────────────────────────

async function waitForPlanToLand(
  targetPlan: string,
  updateUser: (u: any) => void,
  timeoutMs = 30_000,
): Promise<boolean> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      const res = await authApi.me();
      if (res.user?.currentPlan === targetPlan || res.user?.currentPlan === targetPlan) {
        updateUser(res.user);
        return true;
      }
    } catch { /* keep trying */ }
    await new Promise((r) => setTimeout(r, 1500));
  }
  return false;
}

// ── Payment form (inside StripeProvider) ────────────────────────────────────

function SubscribeForm({
  planTier,
  billingCycle,
  onSuccess,
}: {
  planTier: PaidPlanTier;
  billingCycle: Cycle;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [statusText, setStatusText] = useState("");

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setStatusText("Saving your card…");
    try {
      // Validate card details
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message || "Card details invalid");
        setSubmitting(false);
        setStatusText("");
        return;
      }

      // Confirm SetupIntent to get the paymentMethodId
      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url:
            window.location.origin +
            `/subscribe?plan=${planTier}&cycle=${billingCycle}`,
        },
        redirect: "if_required",
      });
      if (setupError) {
        toast.error(setupError.message || "Card setup failed");
        setSubmitting(false);
        setStatusText("");
        return;
      }
      if (!setupIntent || typeof setupIntent.payment_method !== "string") {
        toast.error("Card setup did not return a payment method. Please try again.");
        setSubmitting(false);
        setStatusText("");
        return;
      }

      // Create the subscription on the backend
      setStatusText("Creating your subscription…");
      const sub = await billingApi.createSubscription({
        plan: planTier,
        billingCycle,
        paymentMethodId: setupIntent.payment_method,
      });

      // If SCA is required, confirm the first payment
      if (sub.clientSecret && sub.status !== "active" && sub.status !== "trialing") {
        setStatusText("Confirming first payment…");
        const { error: payError } = await stripe.confirmCardPayment(
          sub.clientSecret,
          { payment_method: setupIntent.payment_method },
        );
        if (payError) {
          toast.error(payError.message || "First payment failed");
          setSubmitting(false);
          setStatusText("");
          return;
        }
      }

      onSuccess();
    } catch (err: any) {
      toast.error(err?.message || "Subscription failed");
      setStatusText("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentElement options={{ layout: "tabs" }} />
      <Button
        variant="primary"
        fullWidth
        loading={submitting}
        disabled={!stripe || submitting}
        onClick={handleSubmit}
      >
        {submitting ? "Processing…" : "Subscribe"}
      </Button>
      {statusText && (
        <p className="text-center text-sm text-fgMuted">{statusText}</p>
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────

export function SubscribePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const updateUser = useAuthStore((s) => s.updateUser);
  const toast = useToast();

  const [plans, setPlans] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    billingApi
      .plans()
      .then((res) => {
        const list = res.plans || [];
        setPlans(list);

        const wantedPlan = searchParams.get("plan");
        const wantedCycle = searchParams.get("cycle") as Cycle | null;
        if (wantedPlan) {
          const match = list.find((p: any) => p.tier === wantedPlan);
          if (match) setSelected(match);
        } else if (list.length > 0) {
          setSelected(list[0]);
        }
        // (P1) ?cycle=annually only where an annual price is configured for every plan
        if (wantedCycle === "monthly" || (wantedCycle === "annually" && annualOffered(list.map((p: any) => p.price)))) {
          setCycle(wantedCycle);
        }
      })
      .catch((err: any) => toast.error(err?.message || "Could not load plans"))  // catch-reason: the error is shown to the person (a toast)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (session 10, P1) the configured Stripe amounts (GET /billing/plans `price`), never a discount assumed here
  const annual = useMemo(() => annualOffered(plans.map((p: any) => p.price)), [plans]);
  const saving = useMemo(() => annualSaving(plans.map((p: any) => p.price).filter(Boolean)), [plans]);
  const priceDisplay = useMemo(() => {
    if (!selected?.price) return "";
    const s = shownPrice(selected.price, cycle);
    return s.billedYearly ? `${s.perMonth}/mo (billed ${s.billedYearly}/yr)` : `${s.perMonth}/mo`;
  }, [selected, cycle]);

  const startCheckout = async () => {
    if (!selected) return;
    setPreparing(true);
    try {
      const res = await billingApi.setupIntent();
      setClientSecret(res.clientSecret);
    } catch (err: any) {
      toast.error(err?.message || "Could not start checkout");
    } finally {
      setPreparing(false);
    }
  };

  const handleSuccess = async () => {
    if (!selected) return;
    toast.info("Subscription created — updating your account…");
    const landed = await waitForPlanToLand(
      selected.tier,
      updateUser,
    );
    if (landed) {
      toast.success("You're subscribed!");
    } else {
      toast.info("Payment received. Your plan will update in a moment.");
    }
    navigate("/billing?subscribed=1");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <h1 className="text-xl font-bold text-fg">No plans available</h1>
            <p className="mt-2 text-sm text-fgMuted">
              Subscription plans haven't been configured yet. Please check back later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <h1 className="mb-1 text-2xl font-bold text-fg">Subscribe to a plan</h1>
      <p className="mb-6 text-sm text-fgMuted">
        Cancel anytime from your billing settings.
      </p>

      {/* Billing cycle toggle (P1: only where an annual price is configured) */}
      {annual && <div className="mb-6 inline-flex rounded-lg border border-border bg-surface p-1">
        <button
          type="button"
          onClick={() => { setCycle("monthly"); setClientSecret(null); }}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            cycle === "monthly"
              ? "bg-brand-600 text-white"
              : "text-fgMuted hover:text-fg"
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => { setCycle("annually"); setClientSecret(null); }}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            cycle === "annually"
              ? "bg-brand-600 text-white"
              : "text-fgMuted hover:text-fg"
          }`}
        >
          Annually
          {saving && <Badge variant="success" className="ml-2 text-[10px]">{saving}</Badge>}
        </button>
      </div>}

      {/* Plan cards */}
      <div className="mb-6 grid gap-4 md:grid-cols-3">
        {plans.map((plan: any) => {
          const isSelected = selected?.tier === plan.tier;
          const isCurrent = user?.currentPlan === plan.tier;
          // (audit) an older backend sends no `price`: the card shows "—" instead of throwing
          const shown = plan.price ? shownPrice(plan.price, cycle) : { perMonth: "—", billedYearly: null };
          return (
            <button
              key={plan.tier}
              type="button"
              disabled={isCurrent}
              onClick={() => { setSelected(plan); setClientSecret(null); }}
              className={`text-left rounded-xl border p-5 transition ${
                isSelected
                  ? "border-brand-500 ring-2 ring-brand-500/30 bg-brand-500/5"
                  : isCurrent
                    ? "border-border bg-surface opacity-60 cursor-default"
                    : "border-border bg-surface hover:border-fgMuted"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-fg">{plan.displayName}</span>
                {isCurrent && <Badge variant="info">Current</Badge>}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-fg">{shown.perMonth}</span>
                <span className="text-sm text-fgMuted">/mo</span>
              </div>
              {shown.billedYearly && (
                <p className="mt-1 text-xs text-fgMuted">
                  Billed {shown.billedYearly}/yr
                </p>
              )}
              <ul className="mt-4 space-y-1.5 text-sm text-fgMuted">
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  {plan.videosPerMonth} videos/month
                </li>
                {shownMaxLength(plan.maxVideoLengthSeconds) && (
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    {`Up to ${shownMaxLength(plan.maxVideoLengthSeconds)} videos`}
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                  <span className="capitalize">{plan.imageModel.replace("-", " ")} images</span>
                </li>
                {plan.allowYoutubePublish && (
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    Direct YouTube publish
                  </li>
                )}
              </ul>
            </button>
          );
        })}
      </div>

      {/* Checkout area */}
      {selected && user?.currentPlan !== selected.tier && (
        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center justify-between">
              <span className="text-fgMuted">
                {selected.displayName} · {cycle === "annually" ? "Annual" : "Monthly"}
              </span>
              <span className="font-bold text-fg">{priceDisplay}</span>
            </div>

            {!clientSecret && (
              <Button
                variant="primary"
                fullWidth
                loading={preparing}
                onClick={startCheckout}
              >
                {preparing ? "Preparing…" : "Continue to payment"}
              </Button>
            )}

            {clientSecret && (
              <StripeProvider clientSecret={clientSecret}>
                <SubscribeForm
                  planTier={selected.tier as PaidPlanTier}
                  billingCycle={cycle}
                  onSuccess={handleSuccess}
                />
              </StripeProvider>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
